import React, { useState } from "react";
import { Button, Table, Pagination, Form, Dropdown } from "react-bootstrap";
import "./css/Audit.css";
import AuditModal from "./AuditModal";
import Icon from "../generic/Icon";

const AuditTable = ({
  data,
  showCarrierName,
  showDiversityGroup,
  showSiteId,
  objectType,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedOptions, setSelectedOptions] = useState({
    Timestamp: { start: "", end: "" },
    Type: [],
    "Site/Structure ID": [],
    Identifier: [],
    ParentCarrier: [],
    DiversityGroupName: [],
    Circuit: [],
    Action: [],
    User: [],
  });
  const [openDropdown, setOpenDropdown] = useState(null);

  const getFilteredData = () => {
    if (!data) return [];
    return data.filter((row) => {
      const typeMatch =
        selectedOptions.Type.length === 0 ||
        selectedOptions.Type.includes(row.type);
      const actionMatch =
        selectedOptions.Action.length === 0 ||
        selectedOptions.Action.includes(row.action);
      const userMatch =
        selectedOptions.User.length === 0 ||
        selectedOptions.User.includes(row.userName);
      const siteStructureIdValue =
        row.type === "Site"
          ? `Site ID - ${row.parentId}`
          : row.type === "Structure" || row.type === "Circuit"
            ? `Structure ID - ${row.parentId}`
            : row.parentId;

      const siteMatch =
        selectedOptions["Site/Structure ID"].length === 0 ||
        selectedOptions["Site/Structure ID"].includes(siteStructureIdValue);
      const identifierMatch =
        selectedOptions.Identifier.length === 0 ||
        selectedOptions.Identifier.includes(row.identifier);
      const parentCarrierMatch =
        selectedOptions.ParentCarrier.length === 0 ||
        selectedOptions.ParentCarrier.includes(row.carrierName);
      const diversityGroupMatch =
        selectedOptions.DiversityGroupName.length === 0 ||
        selectedOptions.DiversityGroupName.includes(row.identifier);
      const circuitMatch =
        selectedOptions.Circuit.length === 0 ||
        selectedOptions.Circuit.includes(row.circuit);
      const timestampMatch = (() => {
        const { start, end } = selectedOptions.Timestamp || {};
        if (!start && !end) return true;
        const rowDate = new Date(row.timestamp * 1000);
        rowDate.setHours(0, 0, 0, 0);

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        if (startDate) startDate.setHours(0, 0, 0, 0);
        if (endDate) endDate.setHours(23, 59, 59, 999);

        if (startDate && endDate) {
          return rowDate >= startDate && rowDate <= endDate;
        } else if (startDate) {
          return rowDate >= startDate;
        } else if (endDate) {
          return rowDate <= endDate;
        }
        return true;
      })();
      return (
        typeMatch &&
        actionMatch &&
        userMatch &&
        siteMatch &&
        identifierMatch &&
        parentCarrierMatch &&
        diversityGroupMatch &&
        circuitMatch &&
        timestampMatch
      );
    });
  };
  const DateRangeDropDown = ({
    title,
    selectedOptions,
    setSelectedOptions,
    openDropdown,
    setOpenDropdown,
    auditData,
  }) => {
    const isOpen = openDropdown === title;
    const [tempStartDate, setTempStartDate] = useState(
      selectedOptions[title]?.start || "",
    );
    const [tempEndDate, setTempEndDate] = useState(
      selectedOptions[title]?.end || "",
    );

    // Calculate min and max dates from audit data
    let minDate = "";
    let maxDate = "";
    if (auditData && auditData.length > 0) {
      const dates = auditData.map((item) => new Date(item.timestamp * 1000));
      const minDateObj = new Date(Math.min(...dates));
      const maxDateObj = new Date(Math.max(...dates));
      if (!isNaN(minDateObj.getTime())) {
        minDate = minDateObj.toISOString().split("T")[0];
      }
      if (!isNaN(maxDateObj.getTime())) {
        maxDate = maxDateObj.toISOString().split("T")[0];
      }
    }

    const isFilterActive =
      selectedOptions[title]?.start || selectedOptions[title]?.end;

    const handleApply = () => {
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: {
          start: tempStartDate,
          end: tempEndDate,
        },
      }));
      setOpenDropdown(null);
    };

    const handleClearFilters = () => {
      setTempStartDate("");
      setTempEndDate("");
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: { start: "", end: "" },
      }));
    };

    return (
      <Dropdown
        align="end"
        show={isOpen}
        onToggle={(isOpen) => {
          if (isOpen) {
            setOpenDropdown(title);
            // Reset temp values to current filter values when opening
            setTempStartDate(selectedOptions[title]?.start || "");
            setTempEndDate(selectedOptions[title]?.end || "");
          } else {
            setOpenDropdown(null);
          }
        }}
      >
        <Dropdown.Toggle
          className="no-caret"
          as="span"
          style={{
            cursor: "pointer",
            marginLeft: "5px",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isFilterActive) {
              handleClearFilters();
            } else {
              setOpenDropdown(isOpen ? null : title);
            }
          }}
        >
          <Icon
            name={isFilterActive ? "removeFilter" : "filter"}
            overrides={{
              tooltip: isFilterActive ? "Clear date filter" : "Filter by date",
              tooltipPlacement: "top",
              iconColor: "#FFFFFF",
              style: {
                cursor: "pointer",
              },
            }}
            id={`filter-dropdown-${title}`}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="dropdown-menu p-3"
          style={{
            minWidth: "280px",
            zIndex: 1050,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "10px" }}>
            <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
              Start Date
            </Form.Label>
            <Form.Control
              type="date"
              value={tempStartDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ marginBottom: "15px" }}>
            <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
              End Date
            </Form.Label>
            <Form.Control
              type="date"
              value={tempEndDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                handleClearFilters();
                setOpenDropdown(null);
              }}
              style={{ flex: 1 }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleApply}
              style={{ flex: 1 }}
            >
              Apply
            </button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const MyDropDown = ({
    title,
    filterData = [],
    selectedOptions,
    setSelectedOptions,
    openDropdown,
    setOpenDropdown,
  }) => {
    const [dropDownFilter, setDropDownFilter] = useState("");
    const isOpen = openDropdown === title;

    const displayedData = (filterData || []).filter((item) =>
      item.name?.toLowerCase().includes(dropDownFilter.toLowerCase()),
    );

    const isFilterActive = selectedOptions[title]?.length > 0;

    const handleCheckboxChange = (key, item) => {
      setSelectedOptions((prev) => ({
        ...prev,
        [key]: prev[key]?.includes(item)
          ? prev[key].filter((i) => i !== item)
          : [...(prev[key] || []), item],
      }));
    };

    const handleClearFilters = () => {
      setSelectedOptions((prev) => ({
        ...prev,
        [title]: [],
      }));
    };

    return (
      <Dropdown
        align="end"
        show={isOpen}
        onToggle={(isOpen) => {
          if (isOpen) {
            setOpenDropdown(title);
          } else {
            setOpenDropdown(null);
          }
        }}
      >
        <Dropdown.Toggle
          className="no-caret"
          as="span"
          style={{
            cursor: "pointer",
            marginLeft: "5px",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isFilterActive) {
              // Clear filters if active
              handleClearFilters();
            } else {
              // Toggle dropdown if no filters are active
              setOpenDropdown(isOpen ? null : title);
            }
          }}
        >
          <Icon
            name={isFilterActive ? "removeFilter" : "filter"}
            overrides={{
              tooltip: isFilterActive ? "Clear filters" : "Filter table",
              tooltipPlacement: "top",
              iconColor: "#FFFFFF",
              style: {
                cursor: "pointer",
              },
            }}
            id={`filter-dropdown-${title}`}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="dropdown-menu w-100 p-2"
          style={{
            maxHeight: "250px",
            minWidth: "fit-content",
            overflow: "auto",
            zIndex: 1050,
          }}
        >
          <Form.Control
            type="text"
            placeholder="Filter..."
            value={dropDownFilter}
            onChange={(e) => setDropDownFilter(e.target.value)}
          />
          <hr className="my-1" />

          <Form.Check
            type="checkbox"
            label="Select All"
            checked={selectedOptions[title]?.length === filterData?.length}
            onChange={() => {
              setSelectedOptions((prev) => ({
                ...prev,
                [title]:
                  prev[title]?.length === filterData?.length
                    ? []
                    : (filterData || []).map((s) => s.name),
              }));
            }}
            className="px-3 ms-1 fw-bold"
          />
          <hr className="my-1" />

          {displayedData.map((item, index) => (
            <Form.Check
              key={index}
              type="checkbox"
              label={item.name}
              checked={selectedOptions[title]?.includes(item.name) || false}
              onChange={() => handleCheckboxChange(title, item.name)}
              className="px-3 ms-1"
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };
  const handleButtonClick = (row) => {
    const rowWithObjectType = { ...row, objectType: objectType };
    setSelectedRow(rowWithObjectType);
    setShowModal(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    const filtered = getFilteredData();
    if (!filtered) return [];
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // For timestamp, sort numerically
      if (sortConfig.key === "timestamp") {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }

      // For strings, sort case-insensitive
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const sortedData = getSortedData();

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 if data or itemsPerPage changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data, itemsPerPage]);

  return (
    <>
      <div
        className="audit-table-wrapper"
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <div
          className="audit-table-container"
          style={{ flex: 1, overflow: "auto", minHeight: 0 }}
        >
          <Table striped bordered className="audit-table">
            <thead>
              <tr>
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("timestamp")}
                    >
                      Timestamp{" "}
                      {sortConfig.key === "timestamp"
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </span>
                    <DateRangeDropDown
                      title="Timestamp"
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                      auditData={data}
                    />
                  </div>
                </th>
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("type")}
                    >
                      Type{" "}
                      {sortConfig.key === "type"
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </span>
                    <MyDropDown
                      title="Type"
                      filterData={[...new Set(data.map((row) => row.type))]
                        .filter(Boolean)
                        .map((type) => ({ name: type }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        handleSort(
                          showDiversityGroup
                            ? "identifier"
                            : showCarrierName
                              ? "carrierName"
                              : showSiteId
                                ? "parentId"
                                : "identifier",
                        )
                      }
                    >
                      {showDiversityGroup
                        ? "Diversity Group Name"
                        : showCarrierName
                          ? "Parent Carrier"
                          : showSiteId
                            ? "Site/Structure ID"
                            : "Identifier"}
                      {sortConfig.key ===
                      (showDiversityGroup
                        ? "identifier"
                        : showCarrierName
                          ? "carrierName"
                          : showSiteId
                            ? "parentId"
                            : "identifier")
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </span>
                    <MyDropDown
                      title={
                        showDiversityGroup
                          ? "DiversityGroupName"
                          : showCarrierName
                            ? "ParentCarrier"
                            : showSiteId
                              ? "Site/Structure ID"
                              : "Identifier"
                      }
                      filterData={
                        showDiversityGroup
                          ? [...new Set(data.map((row) => row.identifier))]
                              .filter(Boolean)
                              .map((val) => ({ name: val }))
                          : showCarrierName
                            ? [...new Set(data.map((row) => row.carrierName))]
                                .filter(Boolean)
                                .map((val) => ({ name: val }))
                            : showSiteId
                              ? [
                                  ...new Set(
                                    data.map((row) =>
                                      row.type === "Site"
                                        ? `Site ID - ${row.parentId}`
                                        : row.type === "Structure" ||
                                            row.type === "Circuit"
                                          ? `Structure ID - ${row.parentId}`
                                          : row.parentId,
                                    ),
                                  ),
                                ]
                                  .filter(Boolean)
                                  .map((val) => ({ name: val }))
                              : [...new Set(data.map((row) => row.identifier))]
                                  .filter(Boolean)
                                  .map((val) => ({ name: val }))
                      }
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                {showDiversityGroup && (
                  <th>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("circuit")}
                      >
                        Circuit Vendor ID{" "}
                        {sortConfig.key === "circuit"
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </span>
                      <MyDropDown
                        title="Circuit Vendor ID"
                        filterData={[...new Set(data.map((row) => row.circuit))]
                          .filter(Boolean)
                          .map((circuit) => ({ name: circuit }))}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </div>
                  </th>
                )}
                {showSiteId && (
                  <th>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("identifier")}
                      >
                        Identifier{" "}
                        {sortConfig.key === "identifier" && !showDiversityGroup
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </span>
                      <MyDropDown
                        title="Identifier"
                        filterData={[
                          ...new Set(data.map((row) => row.identifier)),
                        ]
                          .filter(Boolean)
                          .map((identifier) => ({ name: identifier }))}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </div>
                  </th>
                )}
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("action")}
                    >
                      Action{" "}
                      {sortConfig.key === "action"
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </span>
                    <MyDropDown
                      title="Action"
                      filterData={[...new Set(data.map((row) => row.action))]
                        .filter(Boolean)
                        .map((action) => ({ name: action }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("userName")}
                    >
                      User{" "}
                      {sortConfig.key === "userName"
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </span>
                    <MyDropDown
                      title="User"
                      filterData={[...new Set(data.map((row) => row.userName))]
                        .filter(Boolean)
                        .map((userName) => ({ name: userName }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {currentItems && currentItems.length > 0 ? (
                currentItems.map((row, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(row.timestamp)}</td>
                    <td>{row.type}</td>
                    <td>
                      {showDiversityGroup
                        ? row.identifier
                        : showCarrierName
                          ? row.carrierName
                          : showSiteId
                            ? row.type === "Site"
                              ? `Site ID - ${row.parentId}`
                              : row.type === "Structure" ||
                                  row.type === "Circuit"
                                ? `Structure ID - ${row.parentId}`
                                : row.parentId
                            : row.identifier}
                    </td>
                    {showSiteId && <td>{row.identifier}</td>}
                    {showDiversityGroup && <td>{row.circuit}</td>}
                    <td>{row.action}</td>
                    <td>{row.userName}</td>
                    <td>
                      {row.type !== "Diversity Group Assignment" && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleButtonClick(row)}
                        >
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={showDiversityGroup ? 7 : showSiteId ? 7 : 6}
                    style={{ textAlign: "center" }}
                  >
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {/* Pagination Controls */}
        {sortedData.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              padding: "8px 0",
              width: "100%",
            }}
          >
            <div>
              Showing {sortedData.length === 0 ? 0 : indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, sortedData.length)} of{" "}
              {sortedData.length} entries
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ width: 100, minHeight: 32, fontSize: "0.95rem" }}
                size="sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
              <Pagination size="sm" style={{ margin: 0 }}>
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  return null;
                })}
                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          </div>
        )}
      </div>
      {showModal && selectedRow && (
        <AuditModal
          show={showModal}
          setShow={setShowModal}
          data={selectedRow}
        />
      )}
    </>
  );
};

export default AuditTable;
